import { ServiceSessionStorageBase } from "@/services/storage/base/service-session-storage-base";
import {
  FileSongStorageData,
  PendingConfigHandler,
} from "@/services/storage/base/storage-abstractions";
import { base64ToBlob, blobToBase64 } from "@/utils/blob-utils";

export type BasePendingConfig = { songFile?: File | null; text?: string };

type PendingConfigStorageData<TConfig> = {
  config: TConfig;
  file?: FileSongStorageData;
  text?: string;
};

export class PendingConfigStorageMixin<TConfig extends BasePendingConfig>
  extends ServiceSessionStorageBase
  implements PendingConfigHandler<TConfig>
{
  private readonly pendingConfigKey = "pendingConfig";

  public async setPendingConfigData(config: TConfig | null): Promise<void> {
    if (config === null) {
      await this.deleteDataDB(this.pendingConfigKey);
      return;
    }

    let base64SongFile;
    if (config.songFile) {
      base64SongFile = await blobToBase64(config.songFile as Blob);
    }

    const jsonData = JSON.stringify({
      config: config,
      file: base64SongFile
        ? { songFile: base64SongFile, title: config.songFile?.name }
        : undefined,
      text: config.text,
    } as PendingConfigStorageData<TConfig>);

    await this.saveDataDB(this.pendingConfigKey, jsonData);
  }

  public async getPendingConfigData(): Promise<TConfig | null> {
    const jsonSplitterConfig = await this.getDataDB(this.pendingConfigKey);
    if (!jsonSplitterConfig) {
      return null;
    }

    const parsedData = JSON.parse(
      jsonSplitterConfig,
    ) as PendingConfigStorageData<TConfig>;

    return {
      ...parsedData.config,
      songFile: parsedData.file
        ? new File(
            [base64ToBlob(parsedData.file.songFile)],
            parsedData.file.title,
          )
        : undefined,
      text: parsedData.text,
    } as TConfig;
  }
}
